import { SEO } from './SEO'

type LegalPageProps = {
  page: 'privacy' | 'legal'
  appName: string
  appUrl: string
}

const updatedAt = 'September 7, 2026'

export function LegalPage({ page, appName, appUrl }: LegalPageProps) {
  const isPrivacy = page === 'privacy'
  const title = isPrivacy ? `${appName} Privacy Policy` : `${appName} Terms of Use`
  const description = isPrivacy
    ? `How ${appName} handles account, palm-image, and subscription information.`
    : `The terms that apply to use of ${appName}.`
  const canonicalUrl = `${appUrl}/${page}`

  return (
    <>
      <SEO title={title} description={description} url={canonicalUrl} />
      <main className="legal-page">
        <article>
          <a className="legal-back" href="/">
            ← Back to {appName}
          </a>
          <h1>{title}</h1>
          <p className="legal-date">Last updated: {updatedAt}</p>

          {isPrivacy ? <PrivacyContent appName={appName} /> : <TermsContent appName={appName} />}

          <p className="legal-contact">
            Questions about these terms? Contact us at <a href="mailto:hello@altuzar.dev">hello@altuzar.dev</a> or through the support option in the {appName} app.
          </p>
        </article>
      </main>
    </>
  )
}

function PrivacyContent({ appName }: { appName: string }) {
  return (
    <div className="legal-copy">
      <p>{appName} provides AI-assisted palm readings and compatibility experiences. This policy explains what information we process when you use the app or website.</p>
      <h2>Information we process</h2>
      <p>We process the information you provide to create and use your account, such as your email address and authentication details. If you choose to create a reading or subscribe to the weekly email, we process the palm image you submit and the reading information generated for you.</p>
      <p>We also process subscription and transaction status information from Apple, Google Play, Stripe, and RevenueCat so that we can provide the subscription features you purchase. Payment-card details are handled by the applicable payment provider; we do not receive or store full card numbers.</p>
      <h2>How we use information</h2>
      <p>We use information to authenticate you, provide and improve readings and app features, maintain security, respond to support requests, and administer subscriptions. Palm images may be processed by our AI service provider solely to generate the requested reading.</p>
      <h2>Service providers</h2>
      <p>We use service providers to operate the service, including Convex for backend and data services, Resend for authentication email, OpenAI for image analysis, RevenueCat for subscription management, and Apple, Google Play, and Stripe for payments. Analytics and error-monitoring providers may be used when enabled in our production environment.</p>
      <h2>Retention and security</h2>
      <p>We retain account and reading information for as long as needed to provide the service, meet legal obligations, resolve disputes, and enforce agreements. For weekly email readings, the unsubscribe link immediately disables delivery and deletes the stored palm image. We use reasonable technical and organizational safeguards, but no online service can guarantee absolute security.</p>
      <h2>Your choices</h2>
      <p>You can manage your subscription through the store or payment provider used for purchase. You may also contact us at <a href="mailto:hello@altuzar.dev">hello@altuzar.dev</a> or through the in-app support option to request help with your account or personal information, subject to applicable law.</p>
      <h2 id="account-deletion">Delete your account and data</h2>
      <p>To delete your Co-Palms account, sign in to the app, open the You tab, and choose Delete Account. Confirming the deletion removes your account profile, saved readings and palm images, comparisons, uploaded files, notifications, and active sign-in credentials. You can also email <a href="mailto:hello@altuzar.dev?subject=Co-Palms%20account%20deletion%20request">hello@altuzar.dev</a> from the address on your account to request deletion.</p>
      <p>Deleting an account does not cancel a subscription purchased through Apple, Google Play, or another payment provider; cancel it with that provider. We may retain transaction records only where required for legal, tax, fraud-prevention, or accounting obligations.</p>
      <h2>Children</h2>
      <p>{appName} is not directed to children under 13, and we do not knowingly collect personal information from children under 13.</p>
      <h2>Changes</h2>
      <p>We may update this policy as the service evolves. We will post the updated policy here and revise the “Last updated” date.</p>
    </div>
  )
}

function TermsContent({ appName }: { appName: string }) {
  return (
    <div className="legal-copy">
      <p>By using {appName}, you agree to these Terms of Use and the <a href="/privacy">Privacy Policy</a>. If you do not agree, do not use the service.</p>
      <h2>Entertainment-only experience</h2>
      <p>{appName} provides AI-generated, entertainment-oriented palm readings and compatibility content. It is not medical, legal, financial, mental-health, or other professional advice. Do not rely on it to make important decisions.</p>
      <h2>Your account and content</h2>
      <p>You are responsible for keeping your account credentials secure and for content you submit. You may submit only images and information that you have the right to use. Do not submit unlawful, harmful, or infringing content.</p>
      <h2>Subscriptions</h2>
      <p>Paid features are offered through subscriptions. Pricing, trial eligibility, renewal, cancellation, and refunds are governed by the store or payment provider used for the purchase and the disclosures shown at checkout. You can manage or cancel a mobile subscription through Apple or Google Play, and a web subscription through its payment-management experience.</p>
      <h2>Acceptable use</h2>
      <p>Do not misuse the service, interfere with its operation, attempt unauthorized access, reverse engineer it except where law permits, or use it to violate another person’s rights.</p>
      <h2>Availability and changes</h2>
      <p>We may modify, suspend, or discontinue features when reasonably necessary. The service is provided on an “as is” and “as available” basis to the extent permitted by law.</p>
      <h2>Limitation of liability</h2>
      <p>To the extent permitted by law, {appName} is not liable for indirect, incidental, special, consequential, or punitive damages arising from use of the service.</p>
      <h2>Changes to these terms</h2>
      <p>We may update these terms by posting a revised version here. Continued use after the effective date of an update means you accept the updated terms.</p>
    </div>
  )
}
